import type { ApiEntityAttachment, ApiPagedResult } from "@tm-food/api-contract";
import { saveBlobAsFile as saveBlobOnDevice } from "@/platform/saveFile";
import type { HttpClient } from "@/shared/api/http";
import {
  buildQueryString,
  mapApiPagedResult,
  toApiQueryParams,
  type ListQueryParams,
  type PagedResult,
} from "@/shared/list/listQueryTypes";

export type EntityAttachment = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  objectKey: string;
  readUri: string | null;
  uploadedByName?: string | null;
};

export function mapEntityAttachment(dto: ApiEntityAttachment): EntityAttachment {
  return { ...dto };
}

export async function listEntityAttachments(
  http: HttpClient,
  entityType: string,
  entityId: string,
): Promise<EntityAttachment[]> {
  const qs = buildQueryString({ entityType, entityId });
  const rows = await http.request<ApiEntityAttachment[]>(`/api/v1/doc/attachments${qs}`);
  return (rows ?? []).map(mapEntityAttachment);
}

export async function uploadEntityAttachment(
  http: HttpClient,
  entityType: string,
  entityId: string,
  file: File,
): Promise<EntityAttachment> {
  const qs = buildQueryString({ entityType, entityId });
  const form = new FormData();
  form.append("file", file);
  const dto = await http.request<ApiEntityAttachment>(`/api/v1/doc/attachments${qs}`, {
    method: "POST",
    body: form,
    formData: true,
  });
  return mapEntityAttachment(dto);
}

export async function deleteEntityAttachment(
  http: HttpClient,
  attachmentId: string,
): Promise<void> {
  await http.request<null>(`/api/v1/doc/attachments/${encodeURIComponent(attachmentId)}`, {
    method: "DELETE",
  });
}

export type AttachmentCatalogItem = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  entityType: string;
  entityId: string;
  createdAt: string;
  entityRef?: string | null;
  entityHint?: string | null;
  uploadedByName?: string | null;
};

export async function listAttachmentCatalog(
  http: HttpClient,
  params: ListQueryParams,
): Promise<PagedResult<AttachmentCatalogItem>> {
  const qs = buildQueryString({
    ...toApiQueryParams({ ...params, status: "all" }),
    ...(params.status && params.status !== "all" ? { contentKind: params.status } : {}),
  });
  const dto = await http.request<ApiPagedResult<AttachmentCatalogItem>>(
    `/api/v1/doc/attachments/catalog${qs}`,
  );
  return mapApiPagedResult(dto, (row) => row);
}

export async function getAttachment(
  http: HttpClient,
  attachmentId: string,
): Promise<AttachmentCatalogItem> {
  return http.request<AttachmentCatalogItem>(
    `/api/v1/doc/attachments/${encodeURIComponent(attachmentId)}`,
  );
}

export async function openEntityAttachmentContent(
  http: HttpClient,
  attachmentId: string,
): Promise<Blob> {
  return http.requestBlob(
    `/api/v1/doc/attachments/${encodeURIComponent(attachmentId)}/content`,
  );
}

/** Browser download, or the system share sheet inside the native shell. */
export function saveBlobAsFile(blob: Blob, fileName: string): Promise<void> {
  return saveBlobOnDevice(blob, fileName || "download");
}

export async function downloadEntityAttachment(
  http: HttpClient,
  attachmentId: string,
  fileName: string,
): Promise<void> {
  const blob = await openEntityAttachmentContent(http, attachmentId);
  await saveBlobAsFile(blob, fileName);
}
